import { Task } from '@lit/task';
import { LDClient, initialize } from 'launchdarkly-js-client-sdk';
import { ReactiveController, ReactiveControllerHost } from 'lit';
import { FlagSet } from '../feature-flags';
import { getFeatureFlagsConfig, getUserInfo, isCompleteFlagConfig } from '../services/menu-data-store';
import { UserInformation } from '../types';

// Module-scoped so every platform-menu element on the page (header, sidebar, ...)
// shares one LD connection and one identify, instead of opening one per element.
// Lives exactly as long as the document — see menu-data-store.ts for the
// lifetime/data-leakage invariant.
let sharedClient: LDClient | undefined;
let sharedFlags: FlagSet = {};
let sharedFlagsResolved = false;
let identifiedKey: string | undefined;
const hosts = new Set<ReactiveControllerHost>();

// `identified` gates readiness only, never the publish: the `change` handler is registered even
// when `ready` fires anonymously, so an anonymous evaluation must not mark flags resolved.
function publishFlags(flags: FlagSet, identified: boolean) {
  sharedFlags = flags;
  if (identified) {
    sharedFlagsResolved = true;
  }
  for (const host of hosts) {
    host.requestUpdate();
  }
}

/**
 * Responsible for setting up launch darkly and
 * providing the feature flags to the consumer.
 */
export class LaunchDarklyController implements ReactiveController {
  private host: ReactiveControllerHost;
  private _userinfo?: UserInformation;

  constructor(host: ReactiveControllerHost) {
    (this.host = host).addController(this);
  }

  public get flags(): FlagSet {
    return sharedFlags;
  }

  /**
   * True once flags have been published for an identified user. `flags` starts as `{}`, so
   * an empty flag set cannot tell "not resolved yet" from "flag absent".
   */
  public get flagsResolved(): boolean {
    return sharedFlagsResolved;
  }

  public hostConnected() {
    hosts.add(this.host);
  }

  public hostDisconnected() {
    hosts.delete(this.host);
  }

  /**
   * Fetch the flag config and user info (via the shared store) and bring the
   * shared client up. Safe to call from every host element: the fetches are
   * promise-deduped, client creation is guarded, and identify is deduped per
   * user@site. Resolves without flags when no config is available — hosts then
   * render without flag-gated content.
   */
  public async bootstrap(): Promise<void> {
    try {
      const [config, info] = await Promise.all([getFeatureFlagsConfig(), getUserInfo()]);
      if (!isCompleteFlagConfig(config)) {
        return;
      }
      this.initializeClient(config.platformClientID, config.baseURI, config.streamURI, config.eventsURI);
      this.userinfo = info;
    } catch {
      // Flag config or user info unavailable: no flags for this page.
    }
  }

  public initializeClient(clientID: string, baseURL: string, streamURL: string, eventsURL: string) {
    if (!sharedClient) {
      console.debug('initializing platform-menu LD connection');
      const client = this.initLaunchDarklyClient(clientID, baseURL, streamURL, eventsURL);
      this.onReady(client);
    }
    // Once per controller instance — reconnect cycles re-run bootstrap(), and
    // every call would otherwise stack another Task onto the host.
    if (!this.identifyTaskCreated) {
      this.identifyTaskCreated = true;
      this.initLaunchDarklyTask();
    }
  }

  private identifyTaskCreated = false;

  set userinfo(info: UserInformation) {
    this._userinfo = info;
    // The identify task re-reads its args on host updates.
    this.host.requestUpdate();
  }

  private initLaunchDarklyClient(clientID: string, baseUrl: string, streamUrl: string, eventsUrl: string): LDClient {
    sharedClient = initialize(
      clientID,
      {
        kind: 'user',
        anonymous: true,
      },
      {
        streaming: true,
        bootstrap: 'localStorage',
        baseUrl,
        streamUrl,
        eventsUrl,
      },
    );
    return sharedClient;
  }

  private initLaunchDarklyTask() {
    new Task(this.host, {
      task: async ([info, client]) => {
        if (info == undefined || client == undefined) {
          this.host.requestUpdate();
          return;
        }

        // Another element already identified this user; flags arrive via publishFlags.
        const key = `${info.userId}@${info.siteId}`;
        if (identifiedKey === key) {
          return;
        }
        identifiedKey = key;

        let flags: FlagSet;
        try {
          flags = await client.identify({
            kind: 'multi',
            user: {
              anonymous: false,
              key: info.userId,
              email: info.email,
              firstName: info.givenName,
              lastName: info.familyName,
            },
            organization: {
              key: info.externalId,
            },
            tenant: {
              key: info.siteId,
            },
          });
        } catch (err) {
          // A failed identify must not block a retry (from this or a sibling element).
          identifiedKey = undefined;
          throw err;
        }

        if (!client.getContext().anonymous) {
          publishFlags(flags, true);
        }

        this.host.requestUpdate();
        return;
      },
      args: () => [this._userinfo, sharedClient] as const,
    });
  }

  private onReady(client: LDClient) {
    client.on('ready', () => {
      if (!client.getContext().anonymous) {
        publishFlags(client.allFlags(), true);
      }

      this.listenForFlagChanges(client);
    });
  }

  private listenForFlagChanges(client: LDClient) {
    client.on('change', () => {
      publishFlags(client.allFlags(), !client.getContext().anonymous);
    });
  }
}
